import { useNavigate } from 'react-router-dom';
import Button from 'src/components/button/Button';
import IconSvg from 'src/assets/icons/IconSvg';
import type { Raffle } from 'src/types/raffle';
import { useCountdown } from 'src/hooks/useCountdown';

interface Props {
  raffle: Raffle;
  userId: string;
  isFavorited?: boolean;
  onToggleFavorite: (raffleId: string) => void;
  onClaimPrize?: (raffleId: string) => void;
}

const satsToBtc = (sats: number) => (sats / 1e8).toFixed(4);

const JoinedRaffleCard = ({ raffle, userId, isFavorited, onToggleFavorite, onClaimPrize }: Props) => {
  const navigate = useNavigate();
  const ticketPriceBtc = satsToBtc(raffle.ticketPriceSats);
  const totalValueBtc = satsToBtc(raffle.totalTicketValue);
  const imageUrl = raffle.ordinalImageUrl || raffle.runesImageUrl || '';
  const isLive = raffle.status === 'LIVE';
  const isEnded = raffle.status === 'ENDED' || raffle.status === 'COMPLETED';

  const userIsWinner = raffle.winners?.some((w) => w.winnerId === userId);
  const userHasClaimed = raffle.winners?.some((w) => w.winnerId === userId && w.claimed);

  const countdown = useCountdown(raffle.endDate);

  return (
    <div className="space-y-2 w-full cursor-pointer" onClick={() => navigate(`/raffle/${raffle.id}`)}>
      <div className="h-[408px] rounded-[6px] relative overflow-hidden bg-grayscale-800">
        <button
          className="absolute bg-brand-black rounded-lg p-2.5 flex items-center justify-center top-5 right-5"
          onClick={e => { e.stopPropagation(); onToggleFavorite(raffle.id); }}
        >
          <IconSvg
            icon="starM"
            className={isFavorited ? 'text-brand-tangerine' : 'text-grayscale-400'}
          />
        </button>

        {imageUrl ? (
          <img src={imageUrl} alt={raffle.name ?? ''} className="object-cover w-full h-full" />
        ) : (
          <div className="flex items-center justify-center w-full h-full text-grayscale-400 text-lg">
            {raffle.prizeType === 'RUNES' ? raffle.runesTokenName : 'No Image'}
          </div>
        )}

        <div className="absolute bottom-3 left-3 right-3 bg-[#10100FAD] rounded h-[36px] px-3 flex items-center gap-2 text-grayscale-300 text-sm font-semibold backdrop-blur-[6px]">
          <span>FP: {ticketPriceBtc} BTC</span>
          <span>|</span>
          <span>TTV: {totalValueBtc} BTC</span>
        </div>
      </div>

      <div className="p-5 space-y-4 rounded-lg bg-grayscale-800">
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <div className="flex-shrink-0 w-6 h-6 bg-white rounded-full"></div>
              <div className="font-medium text-brand-granite">
                {raffle.creator?.id === userId ? 'You' : 'Creator'}
              </div>
            </div>

            {isLive && (
              <div className="flex gap-1 items-center text-sm text-system-success">
                <IconSvg icon="liveS" />
                <span>{countdown}</span>
              </div>
            )}

            {isEnded && (
              <div className="text-sm text-system-error">Ended</div>
            )}
          </div>

          <div className="flex gap-2 items-center">
            <h4 className="text-grayscale-100">{raffle.name ?? 'Untitled Raffle'}</h4>
            <IconSvg icon="verifiedM" className="text-brand-tangerine" />
          </div>
        </div>

        <div className="flex justify-between">
          <div className="space-y-1">
            <div className="text-xs font-medium tracking-[-0.5px] text-brand-granite">Tickets Sold</div>
            <div className="text-lg font-semibold text-grayscale-100">
              {raffle.ticketsSold} / {raffle.supply.toLocaleString()}
            </div>
          </div>

          <div className="space-y-1 text-right">
            <div className="text-xs font-medium tracking-[-0.5px] text-brand-granite">Tickets Price</div>
            <div className="flex gap-1 justify-end items-center text-grayscale-100">
              <IconSvg icon="bitcoinS" />
              <div className="text-lg font-semibold text-grayscale-100">{ticketPriceBtc}</div>
            </div>
          </div>
        </div>

        {isEnded && userIsWinner && !userHasClaimed && (
          <div onClick={e => e.stopPropagation()}>
            <Button variant="primary" size="sm" className="w-full" onClick={() => onClaimPrize?.(raffle.id)}>
              Claim Prize
            </Button>
          </div>
        )}

        {isEnded && userHasClaimed && (
          <Button variant="ghost" size="sm" className="w-full" disabled>
            Claimed
          </Button>
        )}

        {isEnded && !userIsWinner && (
          <button className="w-full text-system-error py-2.5 px-4 rounded-lg text-lg font-semibold bg-brand-black">
            Raffle Ended
          </button>
        )}
      </div>
    </div>
  );
};

export default JoinedRaffleCard;
